import * as pdfjsLib from "pdfjs-dist";

import dayjs from "dayjs";
import "dayjs/locale/de";
import localeData from "dayjs/plugin/localeData";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import TextField from "@mui/material/TextField";
import { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { handleSearch } from "./SearcherPdfsNamesPPB";


dayjs.extend(localeData);
dayjs.locale("de");

try {
	pdfjsLib.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@5.3.31/build/pdf.worker.min.mjs";
} catch (error) {
	console.warn("CDN worker failed, using local fallback...", error);
	pdfjsLib.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs`;
}

const today = dayjs();
const todayFormatted = today.format("DD.MM.YYYY");

export const PlansChoicePPB = () => {
	const [datePicked, setDatePicked] = useState(dayjs());
	const [search, setSearch] = useState("");
	const [searchAttempted, setSearchAttempted] = useState(false);
	const [foundPages, setFoundPages] = useState([]);
	const [loading, setLoading] = useState(false);
	const [multipleBerechnung, setMultipleBerechnung] = useState(false);

	const PERSIST_KEY = "plansFoundPages_ppb_v1";

	useEffect(() => {
		if (!foundPages || foundPages.length === 0) return;
		try {
			const pages = foundPages.map((p) => p.pageNum).join(",");
			const dateStr = datePicked ? datePicked.format("YYYYMMDD") : "";
			const params = new URLSearchParams(window.location.search);
			params.set("pages", pages);
			if (search) params.set("number", search);
			if (dateStr) params.set("date", dateStr);
			window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);

			const payload = { pages, number: search, date: dateStr, ts: Date.now() };
			try {
				localStorage.setItem(PERSIST_KEY, JSON.stringify(payload));
			} catch (e) {
				// ignore storage errors
			}
		} catch (e) {
			// ignore
		}
	}, [foundPages, search, datePicked]);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const params = new URLSearchParams(window.location.search);
				let number = params.get("number");
				const dateStr = params.get("date");
				const pagesParam = params.get("pages");

				if (!number && !dateStr && !pagesParam) {
					const raw = localStorage.getItem(PERSIST_KEY);
					if (raw) {
						const parsed = JSON.parse(raw);
						if (parsed && parsed.ts && Date.now() - parsed.ts < 1000 * 60 * 60 * 24) {
							number = number || parsed.number;
							if (!dateStr && parsed.date) setDatePicked(dayjs(parsed.date, "YYYYMMDD"));
							if (parsed.pages) {
								const ps = new URLSearchParams(window.location.search);
								if (parsed.number) ps.set("number", parsed.number);
								if (parsed.date) ps.set("date", parsed.date);
								if (parsed.pages) ps.set("pages", parsed.pages);
								window.history.replaceState(null, "", `${window.location.pathname}?${ps.toString()}`);
							}
						}
					}
				} else if (dateStr) {
					setDatePicked(dayjs(dateStr, "YYYYMMDD"));
				}

				if (number && !cancelled) {
					setSearch(number);
					setSearchAttempted(true);
					await handleSearch({
						number,
						setFoundPages,
						setLoading,
						renderPageToImage,
						datePicked: dateStr ? dayjs(dateStr, "YYYYMMDD") : datePicked,
						setMultipleBerechnung
					});
				}
			} catch (e) {
				// ignore restore errors
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	async function renderPageToImage(pdf, pageNum) {
		const page = await pdf.getPage(pageNum);
		const viewport = page.getViewport({ scale: 1.5 });
		const canvas = document.createElement("canvas");
		const context = canvas.getContext("2d");
		canvas.height = viewport.height;
		canvas.width = viewport.width;
		await page.render({ canvasContext: context, viewport }).promise;
		return canvas.toDataURL();
	}

	const isDatePickedToday = datePicked.isSame(today, "day");

	return (
		<>
			<Card sx={{ maxWidth: 600, padding: "1rem", margin: "2px", border: "2px solid #003d92", boxShadow: 6 }}>
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<h2 style={{ color: "#003d92" }}>Fahrplan suchen</h2>
					<h4>Heute ist: {todayFormatted}</h4>
				</div>
				<div>
					<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
						<DatePicker
							  label="Datum waehlen"
							value={datePicked}
							onChange={setDatePicked}
							format="DD.MM.YYYY"
							sx={{ mb: 2 }}
						/>
					</LocalizationProvider>
				</div>
				<div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
					<div id="dienst-search-anchor" style={{ display: "flex", justifyContent: "flex-start", alignItems: "start" }}>
						<TextField
							label="Dienst:(4-stellig)"
							variant="outlined"
							size="small"
							value={search}
							onChange={(e) => {
								setSearch(e.target.value.replace(/\D/g, "").slice(0, 4));
								setSearchAttempted(false);
							}}
							sx={{ mb: 2 }}
							InputProps={{
								sx: { fontSize: 28, maxWidth: 140 }
							}}
							inputProps={{ id: "dienst-search-input", maxLength: 4 }}
						/>
						<Button
							variant="contained"
							onClick={() => {
								setSearchAttempted(true);
								handleSearch({
									number: search,
									setFoundPages,
									setLoading,
									renderPageToImage,
									datePicked,
									setMultipleBerechnung
								});
							}}
							disabled={!/^\d{4}$/.test(search)}
							sx={{ mb: 2, ml: 1 }}
						>
							Start
						</Button>
					</div>
					{isDatePickedToday && (
						<Button
							variant="outlined"
							color="primary"
							onClick={() => {
								setSearchAttempted(true);
								handleSearch({
									number: search,
									setFoundPages,
									setLoading,
									renderPageToImage,
									datePicked: datePicked.add(1, "day"),
									setMultipleBerechnung
								});
							}}
							disabled={!/^\d{4}$/.test(search)}
							sx={{ mb: 2, ml: 0 }}
						>
							Start morgen
						</Button>
					)}
				</div>
				<div>
					{loading && (
						<div style={{ display: "flex", alignItems: "center", gap: 8, margin: "16px 0" }}>
							<CircularProgress size={40} />
							  <span>Pdf hat Hunderte von Seiten. Suche laeuft...</span>
						</div>
					)}
				</div>
			</Card>

			{foundPages.length > 0 && (
				<div>
					{foundPages.map(({ pageNum, imageUrl, crewSwap = [] }) => (
						<div key={pageNum} style={{ marginBottom: 16 }}>
							{Array.isArray(crewSwap) && crewSwap.length > 0 && (
								<div style={{ color: "red", fontWeight: "bold", marginBottom: 8, fontSize: 48, lineHeight: 1 }}>
									{crewSwap.map((txt, i) => {
										const raw = String(txt || "");
										const plain = raw.replace(/<[^>]*>/g, "").trim();
										const isUmlauf = /\bumlauf\b/i.test(plain);
										const itemStyle = {
											fontSize: 32,
											color: isUmlauf ? "#004081ff" : "red",
											fontWeight: isUmlauf ? 700 : 600
										};
										return (
											<div key={i} style={itemStyle}>
												{plain}
											</div>
										);
									})}
								</div>
							)}

							<img src={imageUrl} alt={`Seite ${pageNum}`} style={{ width: "100%" }} />
						</div>
					))}
				</div>
			)}

			{!loading && foundPages.length === 0 && searchAttempted && (
				<div style={{ color: "red" }}>Nummer nicht gefunden.</div>
			)}
		</>
	);
};
